import {dateToString, getFirstWeekStartDate, getLastWeekEndDate} from "../../tools";
import {useMemo} from "react";
import MEventsCalendarCell from "../components/MEventsCalendarCell";
import {Col, Row, Spinner, Table} from "react-bootstrap";
import {useCollectionData} from "react-firebase-hooks/firestore";
import {collection, orderBy, query, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {logisticsFBConverter} from "../../views/logistics/LogisticsFBConverter";
import MLogisticsCalendarCell from "./MLogisticsCalendarCell";
import {useAppSelector} from "../../hooks";
import {selectCurrentEmployee, selectCurrentUser} from "../../../features/AppStateSlice";
import MLogisticsDayDetails from "./MLogisticsDayDetails";

interface MLogisticsCalendarProps {
    selectedDay: Date,
    onDaySelect: (day: Date) => void;
}

export default function MLogisticsCalendar({selectedDay, onDaySelect}: MLogisticsCalendarProps) {
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    console.log(currentEmployee);
    const firstViewDate = useMemo(() => getFirstWeekStartDate(selectedDay), [selectedDay]);
    const lastViewDate = useMemo(() => getLastWeekEndDate(selectedDay), [selectedDay]);


    const queryConstraints = useMemo(() => {
        if (!currentEmployee) {
            return null;
        }

        const constraints = [
            where("startDate", ">=", firstViewDate),
            where("startDate", "<", lastViewDate),
            orderBy("startDate", "asc")
        ];

        if (!currentEmployee.isAdmin && !currentEmployee.isSuperAdmin && !currentEmployee.isStore) {
            constraints.push(where("driverId", "==", currentEmployee.id));
        }

        return constraints;
    }, [firstViewDate, lastViewDate, currentEmployee]);

    const logisticsQuery = useMemo(
        () => (queryConstraints
            ? query(
                collection(appFirestore, "logistics"),
                ...queryConstraints
            ).withConverter(logisticsFBConverter)
            : null),
        [queryConstraints]
    );
    const [logistics, loadingLogistics, errorLogistics] = useCollectionData(logisticsQuery);

    const calendarContent = useMemo(() => {
        if (!logistics) {
            return [];
        }

        const calendar = [];
        let currentDay = new Date(firstViewDate);
        let week = [];
        let day = 0;

        while (currentDay <= lastViewDate) {
            if (day === 7) {
                calendar.push(
                    <tr key={"week-" + currentDay.toISOString()}>
                        {week}
                    </tr>
                );
                week = [];
                day = 0;
            }

            const isSelectedDate = currentDay.getDate() === selectedDay.getDate() && currentDay.getMonth() === selectedDay.getMonth();
            const borderWidth = isSelectedDate ? 2 : 1;
            const fontWeight = isSelectedDate ? "bold" : "normal";
            const newDate = new Date(currentDay);
            week.push(
                <td
                    onClick={event => {
                        onDaySelect(newDate);
                    }}
                    key={currentDay.toISOString()}
                    className={`text-center fw-${fontWeight} ` + (currentDay.getMonth() !== selectedDay.getMonth() ? "bg-secondary-light" : isSelectedDate ? "bg-primary-light" : "")}
                    style={{height: "50px", overflow: "hidden", border: `${borderWidth}px solid #000000`}}
                >
                    <MLogisticsCalendarCell
                        day={newDate}
                        events={
                            logistics ? logistics.filter(event =>
                                dateToString(event.startDate) === dateToString(newDate)
                            ) : []
                        }
                        onClick={
                            () => {
                                onDaySelect(newDate);
                            }
                        }
                    />
                </td>
            );
            currentDay = new Date(currentDay.setDate(currentDay.getDate() + 1));
            day++;
        }

        calendar.push(
            <tr key={"week-" + currentDay.toISOString()}>
                {week}
            </tr>
        );

        return calendar;
    }, [logistics, selectedDay]);

    if (loadingLogistics) {
        return <Row>
            <Col className={"text-center"}>
                <Spinner animation={"border"}/>
            </Col>
        </Row>
    }

    if (errorLogistics) {
        return <Row>
            <Col className={"text-center"}>
                <h3 className={"text-danger"}>Грешка при зареждане на логистичните събития!</h3>
            </Col>
        </Row>;
    }

    return (<>
        <Table size={"sm"} bordered>
            <thead>
            <tr>
                <th className={"text-center"} style={{width: "14%"}}>Пон</th>
                <th className={"text-center"} style={{width: "14%"}}>Вто</th>
                <th className={"text-center"} style={{width: "14%"}}>Сря</th>
                <th className={"text-center"} style={{width: "14%"}}>Чет</th>
                <th className={"text-center"} style={{width: "14%"}}>Пет</th>
                <th className={"text-center"} style={{width: "14%"}}>Съб</th>
                <th className={"text-center"} style={{width: "14%"}}>Нед</th>
            </tr>
            </thead>

            <tbody>
            {calendarContent}
            </tbody>
        </Table>

        <MLogisticsDayDetails
            events={logistics ? logistics.filter(event =>
                dateToString(event.startDate) === dateToString(selectedDay)
            ) : []}
            selectedDay={selectedDay}
            onSelectedDayChange={onDaySelect}
        />
    </>);
}
